/**
 * Payroll Review - per-client detail page.
 *
 * For each employee shows:
 *   • current Xero details (classification, age, employment type, hours)
 *   • missing fields (visual + "request from client" button → message)
 *   • suggested Fair Work award + link to the source PDF
 *   • "Check minimum wage" button → calls the LLM lookup, shows result
 *     inline with the cited chunk and a confidence badge
 */

import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Loader2, AlertCircle, ArrowLeft, MessageSquare,
  ShieldCheck, FileText, Calculator, ChevronDown, ChevronRight,
  X, ExternalLink, ShieldAlert, Scale, FileSpreadsheet,
} from 'lucide-react';
import {
  getPayrollReview, lookupMinWage, requestMissingInfo, getEmployeeRaw, setEmployeeGrade, setClientLanguage,
  EmployeeReviewRow, MinWageLookup, EmployeeRawResponse,
} from '@/services/api/payroll';
import { exportPayrollToExcel } from '@/utils/payroll-export';
import { cn } from '@/lib/utils';

export default function PayrollClientDetailPage() {
  const { clientId = '' } = useParams<{ clientId: string }>();
  const queryClient = useQueryClient();
  const { data, isLoading, error } = useQuery({
    queryKey: ['payroll-review', clientId],
    queryFn: () => getPayrollReview(clientId),
    enabled: !!clientId,
    staleTime: 30_000,
  });

  const review = data?.review;
  const [lookups, setLookups] = useState<Record<string, MinWageLookup | 'loading' | 'error'>>({});
  const [requestSent, setRequestSent] = useState<Record<string, boolean>>({});
  const [expandedEmpId, setExpandedEmpId] = useState<string | null>(null);
  const [rawCache, setRawCache] = useState<Record<string, EmployeeRawResponse | 'loading' | 'error'>>({});
  // The employee whose min-wage breakdown modal is open (null = closed).
  const [modalEmpId, setModalEmpId] = useState<string | null>(null);

  const toggleExpand = async (empId: string) => {
    if (expandedEmpId === empId) {
      setExpandedEmpId(null);
      return;
    }
    setExpandedEmpId(empId);
    if (rawCache[empId] && rawCache[empId] !== 'error') return;
    setRawCache(prev => ({ ...prev, [empId]: 'loading' }));
    try {
      const r = await getEmployeeRaw(clientId, empId);
      setRawCache(prev => ({ ...prev, [empId]: r }));
    } catch {
      setRawCache(prev => ({ ...prev, [empId]: 'error' }));
    }
  };

  const runLookup = useMutation({
    mutationFn: ({ employeeId }: { employeeId: string }) => lookupMinWage(clientId, employeeId),
    onMutate: ({ employeeId }) => setLookups(prev => ({ ...prev, [employeeId]: 'loading' })),
    onSuccess: ({ lookup }, { employeeId }) =>
      setLookups(prev => ({ ...prev, [employeeId]: lookup })),
    onError: (_e, { employeeId }) =>
      setLookups(prev => ({ ...prev, [employeeId]: 'error' })),
  });

  // Persist a firm-confirmed grade, then re-run the lookup so the rate /
  // compliance / calculation all refresh deterministically from that grade.
  const [savingGrade, setSavingGrade] = useState<string | null>(null);
  const selectGrade = async (employeeId: string, grade: string, awardCode: string | null) => {
    setSavingGrade(employeeId);
    setLookups(prev => ({ ...prev, [employeeId]: 'loading' }));
    try {
      await setEmployeeGrade(clientId, employeeId, grade, awardCode);
      const { lookup } = await lookupMinWage(clientId, employeeId);
      setLookups(prev => ({ ...prev, [employeeId]: lookup }));
    } catch {
      setLookups(prev => ({ ...prev, [employeeId]: 'error' }));
    } finally {
      setSavingGrade(null);
    }
  };

  const sendRequest = useMutation({
    mutationFn: ({ employeeId }: { employeeId: string }) => requestMissingInfo(clientId, employeeId),
    onSuccess: (_r, { employeeId }) => {
      setRequestSent(prev => ({ ...prev, [employeeId]: true }));
      queryClient.invalidateQueries({ queryKey: ['payroll-review', clientId] });
    },
  });

  // "Calculate all" - run the Fair Work lookup for every employee,
  // sequentially so we don't hammer the LLM/embedding APIs. Shows live
  // progress (done / total) and disables while running. We attempt EVERY
  // row (even ones already done or without a matched award) so the button
  // always produces visible activity and the backend decides the result.
  const [calcProgress, setCalcProgress] = useState<{ done: number; total: number } | null>(null);
  const calcAllRunning = calcProgress !== null;
  const calculateAll = async () => {
    if (!review || calcProgress) return;
    const rows = review.rows;
    setCalcProgress({ done: 0, total: rows.length });
    try {
      for (let i = 0; i < rows.length; i++) {
        const id = rows[i].employee.employeeId;
        // Skip ones already successfully looked up this session.
        if (lookups[id] && lookups[id] !== 'error') {
          setCalcProgress({ done: i + 1, total: rows.length });
          continue;
        }
        setLookups(prev => ({ ...prev, [id]: 'loading' }));
        try {
          const { lookup } = await lookupMinWage(clientId, id);
          setLookups(prev => ({ ...prev, [id]: lookup }));
        } catch (e) {
          console.error('min-wage lookup failed for', id, e);
          setLookups(prev => ({ ...prev, [id]: 'error' }));
        }
        setCalcProgress({ done: i + 1, total: rows.length });
      }
    } finally {
      setCalcProgress(null);
    }
  };

  // Per-client communication language (firm→client messages use it).
  const [savingLang, setSavingLang] = useState(false);
  const saveLanguage = async (language: 'en' | 'fa') => {
    setSavingLang(true);
    try {
      await setClientLanguage(clientId, language);
      // Reflect immediately in the cached review.
      queryClient.setQueryData(['payroll-review', clientId], (old: any) =>
        old?.review ? { ...old, review: { ...old.review, communicationLanguage: language } } : old);
    } catch (e) {
      console.error('Failed to set client language', e);
    } finally {
      setSavingLang(false);
    }
  };

  // Export to Excel - enabled once at least one rate has been calculated.
  const hasCalculatedRates = Object.values(lookups).some(l => l && typeof l !== 'string');
  const [exporting, setExporting] = useState(false);
  const exportExcel = async () => {
    if (!review) return;
    setExporting(true);
    try {
      await exportPayrollToExcel({
        clientName: review.clientName,
        xeroOrgName: review.xeroOrgName,
        rows: review.rows,
        lookups,
      });
    } catch (e) {
      console.error('Excel export failed', e);
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="container mx-auto p-6 max-w-7xl">
      <Link
        to="/panel/payroll"
        className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground mb-3"
      >
        <ArrowLeft className="w-3 h-3" /> Back to all clients
      </Link>

      <header className="mb-5 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold">
            {review?.clientName || 'Payroll Review'}
          </h1>
          {review?.xeroOrgName && (
            <p className="text-sm text-muted-foreground">
              Live from {review.xeroOrgName} · {new Date(review.asOf).toLocaleString('en-AU')}
            </p>
          )}
        </div>
        {review && (
          <div className="flex items-center gap-2">
            <label className="text-xs text-muted-foreground" htmlFor="comm-lang">Client language</label>
            <select
              id="comm-lang"
              value={review.communicationLanguage || 'en'}
              onChange={e => saveLanguage(e.target.value as 'en' | 'fa')}
              disabled={savingLang}
              className="text-xs border rounded-md px-2 py-1.5 bg-card disabled:opacity-60"
              title="Messages sent to this client (e.g. requesting missing info) use this language"
            >
              <option value="en">English</option>
              <option value="fa">فارسی (Persian)</option>
            </select>
          </div>
        )}
      </header>

      {isLoading && <Spinner label="Fetching employees & matching awards…" />}

      {error && (
        <ErrorPanel
          title="Couldn't load payroll review."
          body={(error as Error)?.message || ''}
        />
      )}

      {review?.error === 'reconnect_required' && (
        <ErrorPanel
          title="Xero connection needs payroll access"
          body={`Your accountant needs to reconnect Xero to grant the "${review.missingScope || 'payroll.employees.read'}" scope so we can fetch employees.`}
        />
      )}

      {review?.error === 'payroll_not_enabled' && (
        <ErrorPanel
          title="Xero Payroll isn't enabled for this client"
          body="They need to enable the Payroll module in Xero before we can pull employees."
        />
      )}

      {review?.error === 'no_xero_connection' && (
        <ErrorPanel
          title="No Xero connection for this client"
          body="Connect them to a Xero org from the Clients page first."
        />
      )}

      {review && !review.error && (
        <>
          <SummaryStrip summary={review.summary} />

          <div className="mt-3 flex items-center justify-between gap-3 flex-wrap">
            <p className="text-xs text-muted-foreground">
              Minimum rates are read live from the indexed Fair Work pay guides. Click a rate to see the calculation and source.
            </p>
            <div className="flex items-center gap-2">
              {hasCalculatedRates && (
                <button
                  onClick={exportExcel}
                  disabled={exporting}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border text-xs font-semibold hover:bg-muted disabled:opacity-60 whitespace-nowrap"
                >
                  {exporting
                    ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Exporting…</>
                    : <><FileSpreadsheet className="w-3.5 h-3.5" /> Export to Excel</>}
                </button>
              )}
              <button
                onClick={calculateAll}
                disabled={calcAllRunning}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-emerald-600 text-white text-xs font-semibold hover:bg-emerald-700 disabled:opacity-70 disabled:cursor-wait whitespace-nowrap"
              >
                {calcProgress
                  ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Calculating rates… {calcProgress.done}/{calcProgress.total}</>
                  : <><Scale className="w-3.5 h-3.5" /> Calculate all Fair Work rates</>}
              </button>
            </div>
          </div>

          <div className="mt-3 bg-card border rounded-lg overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-xs uppercase font-semibold tracking-wider text-muted-foreground">
                <tr>
                  <th className="px-3 py-2.5 text-left">Employee</th>
                  <th className="px-3 py-2.5 text-left">Classification / Type</th>
                  <th className="px-3 py-2.5 text-left">Age · Hours/wk</th>
                  <th className="px-3 py-2.5 text-left">Suggested award</th>
                  <th className="px-3 py-2.5 text-left">Fair Work min rate</th>
                  <th className="px-3 py-2.5 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {review.rows.map(row => (
                  <EmployeeRow
                    key={row.employee.employeeId}
                    row={row}
                    lookup={lookups[row.employee.employeeId]}
                    onLookup={() => runLookup.mutate({ employeeId: row.employee.employeeId })}
                    requestSent={!!requestSent[row.employee.employeeId]}
                    onRequest={() => sendRequest.mutate({ employeeId: row.employee.employeeId })}
                    requestPending={sendRequest.isPending && sendRequest.variables?.employeeId === row.employee.employeeId}
                    expanded={expandedEmpId === row.employee.employeeId}
                    onToggleExpand={() => toggleExpand(row.employee.employeeId)}
                    rawState={rawCache[row.employee.employeeId]}
                    onOpenModal={() => setModalEmpId(row.employee.employeeId)}
                  />
                ))}
                {review.rows.length === 0 && (
                  <tr><td className="px-3 py-6 text-center text-muted-foreground" colSpan={6}>No active or on-leave employees.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {modalEmpId && review && (() => {
        const row = review.rows.find(r => r.employee.employeeId === modalEmpId);
        if (!row) return null;
        const lk = lookups[modalEmpId];
        const awardCode = lk && typeof lk !== 'string' ? lk.awardCode : null;
        return (
          <MinWageModal
            row={row}
            lookup={lk}
            onLookup={() => runLookup.mutate({ employeeId: modalEmpId })}
            onSelectGrade={(grade) => selectGrade(modalEmpId, grade, awardCode)}
            savingGrade={savingGrade === modalEmpId}
            onClose={() => setModalEmpId(null)}
          />
        );
      })()}
    </div>
  );
}

function Spinner({ label }: { label: string }) {
  return (
    <div className="flex items-center gap-2 text-muted-foreground py-12 justify-center">
      <Loader2 className="w-4 h-4 animate-spin" /> {label}
    </div>
  );
}

function ErrorPanel({ title, body }: { title: string; body: string }) {
  return (
    <div className="bg-rose-50 dark:bg-rose-950/30 border border-rose-200 dark:border-rose-900/60 rounded-md p-4 flex items-start gap-2 text-rose-800 dark:text-rose-200">
      <AlertCircle className="w-4 h-4 mt-0.5" />
      <div>
        <p className="font-medium">{title}</p>
        {body && <p className="text-xs mt-1">{body}</p>}
      </div>
    </div>
  );
}

function SummaryStrip({ summary }: { summary: { total: number; active: number; onLeave: number; needsReview: number; awardMatched: number } }) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-2">
      <Stat label="Employees" value={summary.total} />
      <Stat label="Active" value={summary.active} tone="ok" />
      <Stat label="On leave" value={summary.onLeave} />
      <Stat label="Need info from client" value={summary.needsReview} tone={summary.needsReview > 0 ? 'warn' : 'ok'} />
      <Stat label="Award matched" value={summary.awardMatched} tone={summary.awardMatched === summary.total ? 'ok' : 'warn'} />
    </div>
  );
}

function Stat({ label, value, tone }: { label: string; value: number; tone?: 'ok' | 'warn' }) {
  return (
    <div className="bg-card border rounded-md px-3 py-2">
      <p className="text-[10px] uppercase font-semibold tracking-wider text-muted-foreground">{label}</p>
      <p className={cn(
        'text-xl font-bold leading-tight',
        tone === 'ok'   && 'text-emerald-600',
        tone === 'warn' && 'text-amber-600',
      )}>{value}</p>
    </div>
  );
}

function EmployeeRow({
  row, lookup, onLookup, requestSent, onRequest, requestPending,
  expanded, onToggleExpand, rawState, onOpenModal,
}: {
  row: EmployeeReviewRow;
  lookup?: MinWageLookup | 'loading' | 'error';
  onLookup: () => void;
  requestSent: boolean;
  onRequest: () => void;
  requestPending: boolean;
  expanded: boolean;
  onToggleExpand: () => void;
  rawState?: EmployeeRawResponse | 'loading' | 'error';
  onOpenModal: () => void;
}) {
  const emp = row.employee;
  const name = `${emp.firstName} ${emp.lastName}`.trim() || '(no name)';
  const award = row.suggestedAward;
  const missing = row.missingFields;
  return (
    <>
    <tr className="border-t align-top">
      <td className="px-3 py-3">
        <div className="flex items-start gap-2">
          <button
            onClick={onToggleExpand}
            className="mt-0.5 text-muted-foreground hover:text-foreground"
            title="Show all Xero fields"
            aria-label={expanded ? 'Collapse details' : 'Expand details'}
          >
            {expanded ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
          </button>
          <div>
            <p className="font-medium">{name}</p>
            <p className="text-[11px] text-muted-foreground">{emp.jobTitle || '-'}</p>
            {emp.status === 'LEAVE' && <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 mt-1 inline-block">On leave</span>}
          </div>
        </div>
      </td>
      <td className="px-3 py-3">
        <p className="text-xs">{emp.classification || <span className="text-rose-600 font-semibold">Missing</span>}</p>
        <p className="text-[11px] text-muted-foreground">
          {emp.employmentBasis || <span className="text-rose-600">Type?</span>}
          {emp.payBasis && ` · ${emp.payBasis.toLowerCase()}`}
          {emp.isApprentice && ' · apprentice'}
        </p>
      </td>
      <td className="px-3 py-3 text-xs">
        <p>{emp.ageYears != null ? `${emp.ageYears} yrs` : <span className="text-rose-600 font-semibold">DOB missing</span>}</p>
        <p className="text-muted-foreground">{emp.hoursPerWeek != null ? `${emp.hoursPerWeek}h/wk` : <span className="text-rose-600">Hours?</span>}</p>
        {emp.currentPayRate != null && (
          <p className="text-emerald-700 font-semibold mt-0.5">${emp.currentPayRate.toFixed(2)}/hr now</p>
        )}
      </td>
      <td className="px-3 py-3 text-xs">
        {award.code ? (
          <>
            <p className="font-medium">{award.code}</p>
            <p className="text-[11px] text-muted-foreground line-clamp-2">{award.name}</p>
            <ConfidenceBadge level={award.confidence} />
            {award.sourcePdfUrl && (
              <a
                href={award.sourcePdfUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[11px] text-emerald-700 hover:underline inline-flex items-center gap-0.5 mt-1"
              >
                <FileText className="w-2.5 h-2.5" /> Open pay guide
              </a>
            )}
          </>
        ) : (
          <span className="text-muted-foreground italic">No clear match</span>
        )}
      </td>
      <td className="px-3 py-3 text-xs min-w-[170px]">
        {lookup === 'loading' && (
          <span className="inline-flex items-center gap-1 text-muted-foreground">
            <Loader2 className="w-3 h-3 animate-spin" /> Looking up…
          </span>
        )}
        {lookup === 'error' && (
          <button onClick={onLookup} className="text-rose-600 hover:underline">Lookup failed - retry</button>
        )}
        {lookup && typeof lookup !== 'string' && (
          <MinRateChip lookup={lookup} currentRate={emp.currentPayRate} onClick={onOpenModal} />
        )}
        {!lookup && (
          <div className="flex flex-col items-start gap-1">
            {row.savedGrade && (
              <span className="inline-flex items-center gap-1 text-[10px] font-medium text-emerald-700 dark:text-emerald-300">
                <ShieldCheck className="w-2.5 h-2.5" /> {row.savedGrade}
              </span>
            )}
            <button
              onClick={onLookup}
              disabled={!award.documentId}
              className="inline-flex items-center gap-1 px-2 py-1 rounded border text-[11px] hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed"
              title={!award.documentId ? 'No matching award - cannot look up' : 'Calculate the Fair Work minimum rate'}
            >
              <Calculator className="w-3 h-3" /> {row.savedGrade ? 'Show rate' : 'Check min rate'}
            </button>
          </div>
        )}
      </td>
      <td className="px-3 py-3 text-right">
        {missing.length > 0 ? (
          requestSent ? (
            <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-700">
              <ShieldCheck className="w-3 h-3" /> Request sent
            </span>
          ) : (
            <button
              onClick={onRequest}
              disabled={requestPending}
              className="inline-flex items-center gap-1 px-2 py-1 rounded border text-[11px] hover:bg-muted whitespace-nowrap"
              title={`Ask the client to update: ${missing.join(', ')}`}
            >
              {requestPending
                ? <><Loader2 className="w-3 h-3 animate-spin" /> Sending…</>
                : <><MessageSquare className="w-3 h-3" /> Request {missing.length} {missing.length === 1 ? 'field' : 'fields'}</>}
            </button>
          )
        ) : (
          <span className="inline-flex items-center gap-1 text-[11px] text-muted-foreground">
            <ShieldCheck className="w-3 h-3" /> Complete
          </span>
        )}
      </td>
    </tr>
    {expanded && (
      <tr className="border-t bg-muted/30">
        <td colSpan={6} className="px-4 py-4">
          <RawDataPanel state={rawState} normalized={emp} />
        </td>
      </tr>
    )}
    </>
  );
}

function RawDataPanel({
  state, normalized,
}: {
  state?: EmployeeRawResponse | 'loading' | 'error';
  normalized: EmployeeReviewRow['employee'];
}) {
  if (state === 'loading') {
    return (
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <Loader2 className="w-3.5 h-3.5 animate-spin" /> Fetching live data from Xero…
      </div>
    );
  }
  if (state === 'error') {
    return <p className="text-xs text-rose-600">Couldn't load Xero data for this employee.</p>;
  }
  if (!state) return null;
  if (state.error) {
    return <p className="text-xs text-rose-600">Xero: {state.error}{state.message ? ` - ${state.message}` : ''}</p>;
  }

  const normalizedRows = Object.entries(state.normalized || normalized);
  const raw = state.raw || {};

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <div>
        <p className="text-[10px] uppercase font-semibold tracking-wider text-muted-foreground mb-2">
          Normalised fields (what we read into the page)
        </p>
        <div className="bg-card border rounded p-3 max-h-[400px] overflow-auto">
          <table className="w-full text-xs">
            <tbody>
              {normalizedRows.map(([k, v]) => (
                <tr key={k} className="border-b last:border-b-0">
                  <td className="py-1 pr-3 text-muted-foreground whitespace-nowrap align-top">{k}</td>
                  <td className="py-1 break-all">
                    {v === null || v === undefined || v === '' ? (
                      <span className="text-rose-600 italic">empty</span>
                    ) : typeof v === 'object' ? (
                      <code className="text-[10px]">{JSON.stringify(v)}</code>
                    ) : (
                      String(v)
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <div>
        <p className="text-[10px] uppercase font-semibold tracking-wider text-muted-foreground mb-2">
          Raw Xero response (verbatim from <code>/Employees/{'{ID}'}</code>)
        </p>
        <pre className="bg-card border rounded p-3 max-h-[400px] overflow-auto text-[10px] font-mono whitespace-pre-wrap break-all">
{JSON.stringify(raw, null, 2)}
        </pre>
      </div>
    </div>
  );
}

/** Compliance verdict: is the employee's current rate at/above the FW min? */
function complianceOf(currentRate: number | null, minRate: number | null):
  'compliant' | 'below' | 'unknown' {
  if (currentRate == null || minRate == null) return 'unknown';
  // 1c tolerance to avoid float-rounding false negatives.
  return currentRate + 0.01 >= minRate ? 'compliant' : 'below';
}

/** The clickable rate chip shown in the table cell once a lookup exists. */
function MinRateChip({
  lookup, currentRate, onClick,
}: {
  lookup: MinWageLookup;
  currentRate: number | null;
  onClick: () => void;
}) {
  if (lookup.hourlyRate == null) {
    return (
      <button onClick={onClick} className="text-left inline-flex flex-col gap-0.5 hover:opacity-80">
        <span className="text-rose-600 font-semibold">No clear rate</span>
        <span className="text-[10px] text-muted-foreground underline">View details</span>
      </button>
    );
  }
  const verdict = complianceOf(currentRate, lookup.hourlyRate);
  const verdictCls =
    verdict === 'compliant' ? 'bg-emerald-50 border-emerald-300 text-emerald-800 hover:bg-emerald-100' :
    verdict === 'below'     ? 'bg-rose-50    border-rose-300    text-rose-800    hover:bg-rose-100' :
                              'bg-muted/50   border-border       text-foreground  hover:bg-muted';
  const grade = lookup.matchedRate?.classification;
  return (
    <button
      onClick={onClick}
      className={cn('inline-flex flex-col items-start gap-0.5 rounded-md border px-2.5 py-1.5 transition-colors text-left max-w-[200px]', verdictCls)}
      title="Click to see how this rate was derived"
    >
      <span className="font-bold text-sm leading-none">
        ${lookup.hourlyRate.toFixed(2)}<span className="text-[10px] font-normal">/hr min</span>
      </span>
      {/* Matched grade - the employee's classification under this award.
          Amber dot = estimated (confidence below high). */}
      {grade && (
        <span className="text-[10px] font-medium leading-tight flex items-start gap-1">
          {lookup.confidence !== 'high' && <span className="mt-0.5 w-1.5 h-1.5 rounded-full bg-amber-500 shrink-0" title="Estimated grade" />}
          {grade}{lookup.confidence !== 'high' && ' (est.)'}
        </span>
      )}
      <span className="text-[10px] flex items-center gap-1">
        {verdict === 'compliant' && <><ShieldCheck className="w-2.5 h-2.5" /> Current ${currentRate?.toFixed(2)} OK</>}
        {verdict === 'below'     && <><ShieldAlert className="w-2.5 h-2.5" /> Current ${currentRate?.toFixed(2)} below</>}
        {verdict === 'unknown'   && <span className="text-muted-foreground">{grade ? 'view details' : `${lookup.employeeCategory} · view details`}</span>}
      </span>
    </button>
  );
}

/**
 * Full breakdown modal. Shows the inputs that drove the lookup, the
 * computed minimum, a compliance comparison against the employee's current
 * Xero rate, and the verbatim pay-guide excerpts the rate came from.
 */
function MinWageModal({
  row, lookup, onLookup, onSelectGrade, savingGrade, onClose,
}: {
  row: EmployeeReviewRow;
  lookup?: MinWageLookup | 'loading' | 'error';
  onLookup: () => void;
  onSelectGrade: (grade: string) => void;
  savingGrade: boolean;
  onClose: () => void;
}) {
  const emp = row.employee;
  const name = `${emp.firstName} ${emp.lastName}`.trim() || '(no name)';
  const award = row.suggestedAward;
  const has = lookup && typeof lookup !== 'string';
  const data = has ? (lookup as MinWageLookup) : null;
  const inputs = data?.employeeInputs;
  const verdict = data ? (data.compliance ?? complianceOf(emp.currentPayRate, data.hourlyRate)) : 'unknown';

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-card border rounded-xl shadow-2xl w-full max-w-2xl max-h-[88vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 bg-card border-b px-5 py-3.5 flex items-start justify-between z-10">
          <div>
            <h2 className="text-base font-bold flex items-center gap-1.5">
              <Scale className="w-4 h-4 text-emerald-600" /> Minimum wage breakdown
            </h2>
            <p className="text-xs text-muted-foreground">{name}{emp.jobTitle ? ` · ${emp.jobTitle}` : ''}</p>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground p-1" aria-label="Close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-5 space-y-5">
          {/* Loading / empty states */}
          {lookup === 'loading' && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground py-8 justify-center">
              <Loader2 className="w-4 h-4 animate-spin" /> Looking up Fair Work rates…
            </div>
          )}
          {lookup === 'error' && (
            <div className="text-center py-6">
              <p className="text-sm text-rose-600 mb-3">The lookup failed.</p>
              <button onClick={onLookup} className="inline-flex items-center gap-1 px-3 py-1.5 rounded border text-xs hover:bg-muted">
                <Calculator className="w-3 h-3" /> Try again
              </button>
            </div>
          )}
          {!lookup && (
            <div className="text-center py-6">
              <p className="text-sm text-muted-foreground mb-3">No lookup run yet for this employee.</p>
              <button
                onClick={onLookup}
                disabled={!award.documentId}
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded border text-xs hover:bg-muted disabled:opacity-50"
              >
                <Calculator className="w-3 h-3" /> Calculate minimum rate
              </button>
            </div>
          )}

          {data && (
            <>
              {/* Headline rate + compliance */}
              <div className={cn(
                'rounded-lg border p-4',
                verdict === 'compliant' ? 'bg-emerald-50 border-emerald-200 dark:bg-emerald-950/30 dark:border-emerald-900/60' :
                verdict === 'below'     ? 'bg-rose-50    border-rose-200    dark:bg-rose-950/30    dark:border-rose-900/60' :
                                          'bg-muted/40    border-border',
              )}>
                {/* Matched grade - the employee's classification under the award */}
                {data.matchedRate && (
                  <div className="mb-3 pb-3 border-b border-black/5 dark:border-white/10">
                    <p className="text-[10px] uppercase font-semibold tracking-wider text-muted-foreground">Matched grade</p>
                    <p className="text-sm font-bold">{data.matchedRate.classification}</p>
                    <p className="text-[11px] text-muted-foreground capitalize">
                      {data.employmentType} {data.employeeCategory}{data.juniorAge ? ` · ${data.juniorAge}` : ''}
                    </p>
                  </div>
                )}
                <div className="flex items-end justify-between gap-4 flex-wrap">
                  <div>
                    <p className="text-[10px] uppercase font-semibold tracking-wider text-muted-foreground">Fair Work minimum</p>
                    <p className="text-2xl font-bold leading-tight">
                      {data.hourlyRate != null ? `$${data.hourlyRate.toFixed(2)}/hr` : 'No clear rate'}
                    </p>
                    {data.weeklyRate != null && (
                      <p className="text-xs text-muted-foreground">≈ ${data.weeklyRate.toFixed(2)}/wk</p>
                    )}
                  </div>
                  <div className="text-right">
                    <p className="text-[10px] uppercase font-semibold tracking-wider text-muted-foreground">Currently paid (Xero)</p>
                    <p className="text-2xl font-bold leading-tight">
                      {emp.currentPayRate != null ? `$${emp.currentPayRate.toFixed(2)}/hr` : '-'}
                    </p>
                  </div>
                </div>
                {verdict !== 'unknown' && (
                  <div className={cn(
                    'mt-3 pt-3 border-t flex items-center gap-1.5 text-sm font-semibold',
                    verdict === 'compliant' ? 'text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-900/60'
                                            : 'text-rose-700    dark:text-rose-300    border-rose-200    dark:border-rose-900/60',
                  )}>
                    {verdict === 'compliant'
                      ? <><ShieldCheck className="w-4 h-4" /> Compliant - paid at or above the minimum
                          {emp.currentPayRate != null && data.hourlyRate != null && ` (+$${(emp.currentPayRate - data.hourlyRate).toFixed(2)}/hr)`}</>
                      : <><ShieldAlert className="w-4 h-4" /> Below minimum - short by ${data.hourlyRate != null && emp.currentPayRate != null ? (data.hourlyRate - emp.currentPayRate).toFixed(2) : '?'}/hr</>}
                  </div>
                )}
                <div className="mt-2"><ConfidenceBadge level={data.confidence} /></div>
              </div>

              {data.missingInputs.length > 0 && (
                <div className="rounded-md bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900/60 px-3 py-2 text-xs text-amber-800 dark:text-amber-200">
                  <span className="font-semibold">Missing inputs that may affect accuracy:</span> {data.missingInputs.join(', ')}
                </div>
              )}

              {/* Award + how it was derived */}
              <div>
                <p className="text-[10px] uppercase font-semibold tracking-wider text-muted-foreground mb-1.5">Award & reasoning</p>
                <div className="bg-muted/30 border rounded-md p-3 text-xs space-y-1.5">
                  {data.awardCode && (
                    <p><span className="font-medium">{data.awardCode}</span> - {data.awardName}</p>
                  )}
                  {data.rationale && <p className="text-muted-foreground">{data.rationale}</p>}
                  {data.sourcePdfUrl && (
                    <a href={data.sourcePdfUrl} target="_blank" rel="noopener noreferrer"
                       className="inline-flex items-center gap-1 text-emerald-700 hover:underline">
                      <ExternalLink className="w-3 h-3" /> Open the Fair Work pay guide
                    </a>
                  )}
                </div>
              </div>

              {/* Calculation breakdown */}
              {data.calculation && data.hourlyRate != null && (
                <div>
                  <p className="text-[10px] uppercase font-semibold tracking-wider text-muted-foreground mb-1.5">Calculation</p>
                  <div className="bg-muted/30 border rounded-md p-3 text-xs space-y-1.5 font-mono">
                    {data.calculation.casualLoadingPct != null && data.calculation.permanentBaseRate != null ? (
                      <p>
                        ${data.calculation.permanentBaseRate.toFixed(2)} base
                        <span className="text-muted-foreground"> (permanent) </span>
                        × {(1 + data.calculation.casualLoadingPct / 100).toFixed(2)}
                        <span className="text-muted-foreground"> ({data.calculation.casualLoadingPct}% casual loading) </span>
                        = <span className="font-bold text-emerald-700 dark:text-emerald-300">${data.hourlyRate.toFixed(2)}/hr</span>
                      </p>
                    ) : (
                      <p>Base rate from the pay-guide table: <span className="font-bold">${data.hourlyRate.toFixed(2)}/hr</span></p>
                    )}
                    {data.calculation.weeklyFromHourly != null && data.calculation.standardHoursPerWeek != null && (
                      <p className="text-muted-foreground">
                        ${data.hourlyRate.toFixed(2)} × {data.calculation.standardHoursPerWeek}h/wk
                        = <span className="text-foreground font-semibold">${data.calculation.weeklyFromHourly.toFixed(2)}/wk</span>
                        {data.calculation.annualEstimate != null && <> · ≈ ${data.calculation.annualEstimate.toLocaleString()}/yr</>}
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* Inputs used */}
              <div>
                <p className="text-[10px] uppercase font-semibold tracking-wider text-muted-foreground mb-1.5">Inputs used (from Xero)</p>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs">
                  <Field label="Classification" value={inputs?.classification ?? emp.classification} />
                  <Field label="Employment" value={inputs?.employmentBasis ?? emp.employmentBasis} />
                  <Field label="Pay basis" value={inputs?.payBasis ?? emp.payBasis} />
                  <Field label="Age" value={(inputs?.ageYears ?? emp.ageYears) != null ? `${inputs?.ageYears ?? emp.ageYears} yrs` : null} />
                  <Field label="Hours/wk" value={(inputs?.hoursPerWeek ?? emp.hoursPerWeek) != null ? `${inputs?.hoursPerWeek ?? emp.hoursPerWeek}h` : null} />
                  <Field label="Apprentice" value={(inputs?.isApprentice ?? emp.isApprentice) ? 'Yes' : 'No'} />
                </div>
              </div>

              {/* Grade selector - the firm confirms the grade (the input);
                  the rate then comes deterministically from the Fair Work
                  table. Click any row to set this employee's grade and
                  recalculate. */}
              {data.allRates && data.allRates.length > 0 && (
                <div>
                  <div className="flex items-center justify-between gap-2 mb-1.5">
                    <p className="text-[10px] uppercase font-semibold tracking-wider text-muted-foreground">
                      Select grade - {capitalize(data.employmentType)} {data.employeeCategory}{data.juniorAge ? ` · ${data.juniorAge}` : ''} · {data.awardCode}
                    </p>
                    {data.gradeConfirmed ? (
                      <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase text-emerald-700 dark:text-emerald-300">
                        <ShieldCheck className="w-3 h-3" /> Confirmed
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase text-amber-700 dark:text-amber-300">
                        <ShieldAlert className="w-3 h-3" /> Estimated - confirm grade
                      </span>
                    )}
                  </div>
                  <div className={cn('border rounded-md overflow-hidden relative', savingGrade && 'opacity-60 pointer-events-none')}>
                    {savingGrade && (
                      <div className="absolute inset-0 z-10 flex items-center justify-center bg-card/60">
                        <Loader2 className="w-4 h-4 animate-spin" />
                      </div>
                    )}
                    <table className="w-full text-xs">
                      <thead className="bg-muted/50 text-[10px] uppercase tracking-wider text-muted-foreground">
                        <tr>
                          <th className="px-2.5 py-1.5 text-left">Classification / grade</th>
                          <th className="px-2.5 py-1.5 text-right">Hourly</th>
                          <th className="px-2.5 py-1.5 text-right">Weekly</th>
                          <th className="px-2.5 py-1.5"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.allRates.map((r, i) => {
                          const isMatch = data.matchedRate?.classification === r.classification &&
                                          data.matchedRate?.baseHourlyRate === r.baseHourlyRate;
                          // High confidence (firm-confirmed OR exact) → green;
                          // estimated (pay-inferred) → amber.
                          const highConf = data.confidence === 'high';
                          const matchCls = highConf
                            ? 'bg-emerald-100 dark:bg-emerald-950/50 font-bold ring-2 ring-inset ring-emerald-500'
                            : 'bg-amber-100 dark:bg-amber-950/50 font-bold ring-2 ring-inset ring-amber-500';
                          const tagCls = highConf ? 'text-emerald-700 dark:text-emerald-300' : 'text-amber-700 dark:text-amber-300';
                          const iconCls = highConf ? 'text-emerald-600' : 'text-amber-600';
                          return (
                            <tr
                              key={i}
                              onClick={() => onSelectGrade(r.classification)}
                              className={cn('border-t cursor-pointer hover:bg-muted/40', isMatch && matchCls)}
                              title="Set as this employee's grade"
                            >
                              <td className="px-2.5 py-1.5">
                                <span className="inline-flex items-center gap-1">
                                  {isMatch && <ShieldCheck className={cn('w-3 h-3 shrink-0', iconCls)} />}
                                  {r.classification}
                                </span>
                                {isMatch && (
                                  <span className={cn('ml-1.5 text-[9px] font-bold uppercase', tagCls)}>
                                    ◄ {highConf ? 'this employee' : 'estimated'}
                                  </span>
                                )}
                              </td>
                              <td className="px-2.5 py-1.5 text-right tabular-nums">{r.baseHourlyRate != null ? `$${r.baseHourlyRate.toFixed(2)}` : '-'}</td>
                              <td className="px-2.5 py-1.5 text-right tabular-nums text-muted-foreground">{r.weeklyRate != null ? `$${r.weeklyRate.toFixed(2)}` : '-'}</td>
                              <td className="px-2.5 py-1.5 text-right">
                                <span className="text-[10px] text-[var(--v4-accent-text)] underline opacity-0 group-hover:opacity-100">{isMatch ? '' : 'select'}</span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  <p className="text-[10px] text-muted-foreground mt-1.5 italic">
                    Rates come straight from the Fair Work pay guide. Click a grade to confirm it for this employee - the rate and compliance recalculate from your choice. {data.sourcePdfUrl ? 'Use the source link above to verify.' : ''}
                  </p>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function capitalize(s: string): string {
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : s;
}

function Field({ label, value }: { label: string; value: string | null | undefined }) {
  return (
    <div className="bg-card border rounded px-2 py-1.5">
      <p className="text-[9px] uppercase font-semibold tracking-wider text-muted-foreground">{label}</p>
      <p className="font-medium truncate">{value || <span className="text-rose-600">-</span>}</p>
    </div>
  );
}

function ConfidenceBadge({ level }: { level: 'high' | 'medium' | 'low' | 'none' }) {
  if (level === 'none') return null;
  const cls =
    level === 'high'   ? 'bg-emerald-100 text-emerald-800' :
    level === 'medium' ? 'bg-amber-100   text-amber-800'   :
                         'bg-rose-100    text-rose-800';
  return (
    <span className={cn('inline-block text-[9px] font-bold uppercase px-1 py-0.5 rounded mt-1', cls)}>
      {level} confidence
    </span>
  );
}
